import type { RoomField } from './types';

/**
 * One input per field type — a copy of apps/claro's PreviewInput, deliberately NOT extracted into a
 * shared package: extraction would drag React into the pure, dependency-free @huaca/claro-engine.
 * The two renderers are ~30 lines of proven switch; keeping them as siblings is cheaper than the
 * coupling a shared component would create.
 *
 * `file` is intentionally left disabled here — uploads are Phase 6.
 *
 * onChange always emits a STRING (what the input yields). Number/date fields store their string
 * form; the engine String()-compares for conditions and the server stores `string | number`, so a
 * numeric answer as "42" behaves identically to 42. Clearing yields "" here; the save layer maps ""
 * to null on the wire (the agreed "empty" representation).
 */
export function FieldInput({
  field,
  value,
  onChange,
  onBlur,
}: {
  field: RoomField;
  value: string | number | null | undefined;
  onChange: (v: string) => void;
  /** Present so the autosave controller (Phase 5.5) can hook blur without touching this file again. */
  onBlur?: () => void;
}) {
  const v = value == null ? '' : String(value);
  const id = `room-field-${field.id}`;
  const common = { id, value: v, onChange: (e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value), onBlur };

  switch (field.type) {
    case 'select':
      return (
        <select
          id={id}
          className="room-input"
          value={v}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
        >
          <option value="">— בחרו —</option>
          {(field.options ?? []).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label || o.value}
            </option>
          ))}
        </select>
      );
    case 'number':
      // dir=ltr on the field only: digits read left-to-right inside the RTL page.
      return <input {...common} className="room-input" type="number" inputMode="decimal" dir="ltr" />;
    case 'date':
      return <input {...common} className="room-input" type="date" dir="ltr" />;
    // 'file' is handled by <FileField> (rendered by SectionView), never here — a file is an async
    // upload flow, not a scalar input. FieldInput only sees text/number/date/select.
    default:
      return <input {...common} className="room-input" type="text" />;
  }
}
